/**
 * DISCOM Overview — state map with circle dot overlay + right detail panel.
 * FR-D00 to FR-D03
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import DiscomCircleMap from './DiscomCircleMap';
import { pgvclOverview, pgvclCirclePanelData } from '../../data/mockData';

const prCls = (v: number) =>
  v >= 75 ? 'text-green-600 font-semibold' :
  v >= 65 ? 'text-amber-600 font-semibold' :
            'text-red-600 font-semibold';

function PanelRow({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
      <span className="text-xs text-text-tertiary">{label}</span>
      <span className={`text-xs font-semibold text-right ${cls ?? 'text-text-primary'}`}>{value}</span>
    </div>
  );
}

// DISCOM-level summary for default panel state
const discomPanel = {
  totalPlants:       pgvclOverview.totalPlants,
  capacityMWp:       pgvclOverview.capacityMWp,
  todayMWh:          pgvclOverview.todayMWh,
  specificYield:     pgvclOverview.avgSpecificYield,
  avgPR:             pgvclOverview.avgPR,
  avgCUF:            pgvclOverview.avgCufPct,
  plantAvailability: pgvclOverview.activePct,
  activePlants:      pgvclOverview.activePlants,
  offlinePlants:     pgvclOverview.offlinePlants,
  activeAlarms:      pgvclOverview.sldcFeedStatus === 'LIVE' ? 58 : 0,
};

export default function DiscomOverview() {
  const [selectedCircle, setSelectedCircle] = useState<string | null>(null);

  const circleData = selectedCircle
    ? pgvclCirclePanelData.find(c => c.id === selectedCircle)
    : null;

  const panel = circleData ? {
    totalPlants:       circleData.totalPlants,
    capacityMWp:       circleData.capacityMWp,
    todayMWh:          circleData.todayMWh,
    specificYield:     circleData.specificYield,
    avgPR:             circleData.avgPR,
    avgCUF:            circleData.avgCUF,
    plantAvailability: circleData.plantAvailability,
    activePlants:      circleData.activePlants,
    offlinePlants:     circleData.offlinePlants,
    activeAlarms:      circleData.activeAlarms,
  } : discomPanel;

  const headerLabel = circleData ? circleData.name : 'PGVCL Summary';

  return (
    <div className="p-6 space-y-5">
      {/* ── 6 primary KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Total Plants"          value={pgvclOverview.totalPlants.toLocaleString()}    subtext="PGVCL area" />
        <KPICard title="Installed Capacity"    value={`${pgvclOverview.capacityMWp} MWp`}            subtext="Installed" />
        <KPICard title="Today's Generation"    value={`${pgvclOverview.todayMWh.toLocaleString()} MWh`} subtext="+2.8% vs yesterday" subtextColor="green" />
        <KPICard title="Active Plants"         value={`${pgvclOverview.activePct}%`}                 subtext={`${pgvclOverview.activePlants} of ${pgvclOverview.totalPlants}`} subtextColor="green" />
        <KPICard title="Offline / Fault"       value={pgvclOverview.offlinePlants.toString()}        subtext="Needs attention" subtextColor="red" bgColor="bg-red-50" />
        <KPICard title="Avg Performance Ratio" value={`${pgvclOverview.avgPR}%`}                     subtext="PGVCL average" subtextColor="green" />
      </div>

      {/* ── 2-col: map (65%) + detail panel (35%) ── */}
      <div className="flex gap-4" style={{ minHeight: 440 }}>
        {/* State map + circle dots */}
        <div className="bg-white rounded-card border border-border shadow-card p-4" style={{ width: '65%' }}>
          <SectionDivider
            title="Gujarat — PGVCL Circles"
            subtitle={selectedCircle ? `Selected: ${circleData?.name} — click dot to deselect` : 'Click a circle dot to view details'}
          />
          <div style={{ height: 360 }}>
            <DiscomCircleMap
              onCircleSelect={setSelectedCircle}
              selectedCircle={selectedCircle}
            />
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex flex-col" style={{ width: '35%' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">{headerLabel}</h3>
            {selectedCircle && (
              <button
                onClick={() => setSelectedCircle(null)}
                className="text-text-tertiary hover:text-text-primary transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex-1 space-y-0">
            <PanelRow label="Total Plants"       value={panel.totalPlants.toLocaleString()} />
            <PanelRow label="Installed Capacity" value={`${panel.capacityMWp.toFixed(1)} MWp`} />
            <PanelRow label="Today's Generation" value={`${panel.todayMWh.toLocaleString()} MWh`} />
            <PanelRow label="Specific Yield"     value={`${panel.specificYield.toFixed(2)} kWh/kWp`} />
            <PanelRow
              label="Avg PR"
              value={`${panel.avgPR.toFixed(1)}%`}
              cls={prCls(panel.avgPR)}
            />
            <PanelRow label="Avg CUF"            value={`${panel.avgCUF.toFixed(1)}%`} />
            <PanelRow label="Plant Availability"  value={`${panel.plantAvailability.toFixed(0)}%`} />
            <PanelRow label="Active Plants"       value={panel.activePlants.toLocaleString()} />
            <PanelRow
              label="Offline Plants"
              value={panel.offlinePlants.toLocaleString()}
              cls={panel.offlinePlants > 0 ? 'text-red-600 font-semibold' : 'text-text-primary'}
            />
            <PanelRow
              label="Active Alarms"
              value={panel.activeAlarms.toString()}
              cls={panel.activeAlarms > 0 ? 'text-red-600 font-semibold' : 'text-text-primary'}
            />
          </div>

          {!selectedCircle && (
            <p className="text-xs text-text-tertiary mt-3 pt-3 border-t border-border/40">
              Click any circle dot on the map to view circle-level details.
            </p>
          )}
        </div>
      </div>

      {/* ── 3 secondary KPI cards ── */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg CUF Today</p>
          <p className="text-xl font-bold text-text-primary mt-1">{pgvclOverview.avgCufPct}%</p>
          <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg Specific Yield</p>
          <p className="text-xl font-bold text-text-primary mt-1">{pgvclOverview.avgSpecificYield} kWh/kWp</p>
          <p className="text-xs text-text-secondary mt-1">PGVCL — current month</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Plant Availability</p>
          <p className="text-xl font-bold text-green-600 mt-1">{pgvclOverview.activePct}%</p>
          <p className="text-xs text-text-secondary mt-1">Solar hours basis</p>
        </div>
      </div>
    </div>
  );
}
